import { useState, useMemo, useCallback, useEffect } from 'react';
import { Photo, Video } from '@repo/api/open-calls/graphql';

export enum CandidateMediaType {
  fullBody = 'fullBody',
  portrait = 'portrait',
  profile = 'profile',
  waist = 'waist',
  intro = 'intro',
  runway = 'runway',
}
const candidateMediaTypes = {
  fullBody: CandidateMediaType.fullBody,
  portrait: CandidateMediaType.portrait,
  profile: CandidateMediaType.profile,
  waist: CandidateMediaType.waist,
  intro: CandidateMediaType.intro,
  runway: CandidateMediaType.runway,
};
export type CandidateMedia = {
  id: number;
  url: string;
  type: string;
  thumbnailUrl: string;
  isProcessing?: boolean;
  metadata?: {
    type?: CandidateMediaType;
    extension?: CandidateMediaType;
  };
};

const condenastLogoURL = '/images/logos/conde-nast.png';

export const spotlightMediaTypes = {
  image: 'image',
  video: 'video',
};

export const displayedMediaChangeType = {
  next: 'next',
  previous: 'previous',
};

const getMediaIndexes = (
  mediaChangeType: string,
  currentMediaIndex: number,
  candidateMediaLength: number,
  userSelectedMediaIndex?: number,
) => {
  let previousIndex =
    currentMediaIndex <= 0 ? candidateMediaLength - 1 : currentMediaIndex - 1;
  let nextIndex =
    currentMediaIndex >= candidateMediaLength - 1 ? 0 : currentMediaIndex + 1;

  if (
    mediaChangeType === displayedMediaChangeType.next &&
    userSelectedMediaIndex !== undefined
  ) {
    nextIndex = userSelectedMediaIndex;
  }

  if (
    mediaChangeType === displayedMediaChangeType.previous &&
    userSelectedMediaIndex !== undefined
  ) {
    previousIndex = userSelectedMediaIndex;
  }

  const newCurrentMediaIndex =
    mediaChangeType === displayedMediaChangeType.next
      ? nextIndex
      : previousIndex;

  return {
    previousIndex,
    nextIndex,
    newCurrentMediaIndex,
  };
};

const normalizeCandidateMedia = (videos: Video[], photos: Photo[]) => {
  const media: CandidateMedia[] = [];
  let count = 0;

  photos.forEach((photo: Photo) => {
    const isProcessing = !photo.photo.assetId || !photo.photo.thumbnailUrl;
    media.push({
      id: count,
      url: !isProcessing ? String(photo.photo.assetId) : condenastLogoURL,
      thumbnailUrl: !isProcessing
        ? String(photo.photo.thumbnailUrl)
        : condenastLogoURL,
      isProcessing,
      metadata: {
        extension: photo?.photo?.assetMetaData?.extension,
        type: candidateMediaTypes[
          photo?.photo?.assetMetaData?.type as keyof typeof candidateMediaTypes
        ],
      },
      type: spotlightMediaTypes.image,
    });
    count++;
  });

  videos.forEach((video: Video) => {
    const isVideoTranscoding = video.video.assets.length === 0;

    if (isVideoTranscoding) {
      const loadingVideo = {
        id: count,
        url: condenastLogoURL,
        type: spotlightMediaTypes.image,
        isProcessing: true,
        thumbnailUrl: condenastLogoURL,
      };
      media.push(loadingVideo);
    } else {
      const transcodedVideo = {
        id: count,
        isProcessing: isVideoTranscoding,
        url: video.video.assets.find(
          (asset: { type: string; format: string; url: string }) => {
            if (asset.type === 'video') {
              if (asset.format === 'mpeg4') {
                return asset.url;
              }
            }
            return;
          },
        )?.url,
        thumbnailUrl: video.video.assets.find(
          (asset: { type: string; label: string; url: string }) => {
            if (asset.type === 'image') {
              if (asset.label === 'small') {
                return asset.url;
              }
            }
            return;
          },
        )?.url,
        metadata: {
          type: candidateMediaTypes[
            video?.video?.assetMetaData
              ?.type as keyof typeof candidateMediaTypes
          ],
        },
        type: spotlightMediaTypes.video,
      };
      media.push(transcodedVideo);
    }
    count++;
  });

  return media;
};

export const useCandidateSpotlightMedia = ({
  videos,
  photos,
  initialMedia,
}: {
  videos: Video[];
  photos: Photo[];
  initialMedia?: CandidateMedia;
}) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(
    initialMedia?.id ?? 0,
  );

  const candidateMedia = useMemo(
    () => normalizeCandidateMedia(videos, photos),
    [videos, photos],
  );
  const [currentMedia, setCurrentMedia] = useState(
    candidateMedia[currentMediaIndex]!,
  );
  const [nextMedia, setNextMedia] = useState(currentMedia);

  const handleDisplayedMediaChange = useCallback(
    ({
      mediaChangeType,
      onMediaCleanUp,
      userSelectedMediaIndex,
    }: {
      mediaChangeType: string;
      onMediaCleanUp?: (mediaType: string) => void;
      userSelectedMediaIndex?: number;
    }) => {
      const { previousIndex, nextIndex, newCurrentMediaIndex } =
        getMediaIndexes(
          mediaChangeType,
          currentMediaIndex,
          candidateMedia.length,
          userSelectedMediaIndex,
        );
      const currentMedia = candidateMedia[currentMediaIndex];
      const nextMedia = candidateMedia[nextIndex];
      const previousMedia = candidateMedia[previousIndex];

      const isTargetMediaProcessing =
        mediaChangeType === displayedMediaChangeType.next
          ? nextMedia?.isProcessing
          : previousMedia?.isProcessing;

      onMediaCleanUp?.(currentMedia!.type);

      const currentMediaCondition = {
        [displayedMediaChangeType.previous]:
          previousMedia?.type === spotlightMediaTypes.video ||
          (currentMedia?.type === spotlightMediaTypes.video &&
            previousMedia?.type === spotlightMediaTypes.image) ||
          currentMedia?.isProcessing ||
          isTargetMediaProcessing,
        [displayedMediaChangeType.next]:
          nextMedia?.type === spotlightMediaTypes.video ||
          (currentMedia?.type === spotlightMediaTypes.video &&
            nextMedia?.type === spotlightMediaTypes.image) ||
          currentMedia?.isProcessing ||
          isTargetMediaProcessing,
      }[mediaChangeType];

      if (currentMediaCondition) {
        setCurrentMedia(
          mediaChangeType === displayedMediaChangeType.next
            ? nextMedia!
            : previousMedia!,
        );
      }

      setCurrentMediaIndex(newCurrentMediaIndex);

      const nextMediaCondition = {
        [displayedMediaChangeType.previous]:
          previousMedia?.type === spotlightMediaTypes.image ||
          previousMedia?.isProcessing,
        [displayedMediaChangeType.next]:
          nextMedia?.type === spotlightMediaTypes.image ||
          nextMedia?.isProcessing,
      }[mediaChangeType];

      if (nextMediaCondition) {
        setNextMedia(
          mediaChangeType === displayedMediaChangeType.next
            ? nextMedia!
            : previousMedia!,
        );
      }

      return {
        newMediaId: newCurrentMediaIndex,
      };
    },
    [candidateMedia, currentMediaIndex],
  );

  useEffect(() => {
    setCurrentMedia(candidateMedia[currentMediaIndex]!);
  }, [candidateMedia]);

  return {
    currentMediaIndex,
    candidateMedia,
    currentMedia,
    setCurrentMedia,
    nextMedia,
    handleDisplayedMediaChange,
  };
};
